import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import PortfolioSiteLayout from '@/components/portfolio/PortfolioSiteLayout';
import { portfolioService, type PublicPortfolioBlogDetail } from '@/services/portfolio';
import { getMarketingSiteOrigin } from '@/utils/hostOrigins';

const PortfolioBlogPostPage: React.FC = () => {
  const { portfolioSlug, blogSlug } = useParams<{ portfolioSlug: string; blogSlug: string }>();
  const [post, setPost] = useState<PublicPortfolioBlogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!portfolioSlug || !blogSlug) {
        setError('Post not found');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await portfolioService.getPublicBlogPost(portfolioSlug, blogSlug);
        setPost(data);
        setError(null);
      } catch (err: unknown) {
        setPost(null);
        const msg =
          err &&
          typeof err === 'object' &&
          'response' in err &&
          err.response &&
          typeof err.response === 'object' &&
          'data' in err.response &&
          err.response.data &&
          typeof err.response.data === 'object' &&
          'error' in err.response.data
            ? String((err.response.data as { error?: string }).error)
            : 'Post not found';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [portfolioSlug, blogSlug]);

  if (!portfolioSlug) {
    return null;
  }

  const origin = getMarketingSiteOrigin().replace(/\/$/, '');
  const footerDomain = `${origin}/${encodeURIComponent(portfolioSlug)}`;

  if (loading) {
    return (
      <PortfolioSiteLayout portfolioSlug={portfolioSlug} siteTitle="Loading…" footerDomain={footerDomain}>
        <div className="pub-empty">Loading article…</div>
      </PortfolioSiteLayout>
    );
  }

  if (error || !post) {
    return (
      <div className="public-site-page">
        <div className="public-site">
          <div className="pub-state-card">
            <h1>Article not available</h1>
            <p>{error || 'This post may have been removed.'}</p>
            <Link to={`/${encodeURIComponent(portfolioSlug)}`} className="pub-back-link" style={{ marginTop: 16 }}>
              Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const teacherName =
    [post.teacherFirstName, post.teacherLastName].filter(Boolean).join(' ') || post.tenantName;
  const base = `/${encodeURIComponent(portfolioSlug)}`;

  return (
    <>
      <Helmet>
        <title>
          {post.title} · {post.tenantName}
        </title>
      </Helmet>
      <PortfolioSiteLayout
        portfolioSlug={portfolioSlug}
        siteTitle={post.tenantName}
        tagline={teacherName}
        avatarUrl={post.teacherAvatarUrl}
        avatarAlt={teacherName}
        footerDomain={footerDomain}
        showBlogLink
      >
        <article className="pub-section">
          <Link to={`${base}#blog`} className="pub-back-link">
            ← Back to blog
          </Link>
          <p className="pub-section-label">{post.tenantName}</p>
          <h1 style={{ fontSize: 'clamp(1.35rem, 4vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em' }}>
            {post.title}
          </h1>
          {post.publishedAt && (
            <p className="pub-section-desc" style={{ marginBottom: 20 }}>
              {new Date(post.publishedAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
          {(post.featuredImageUrl ?? '').trim().length > 0 && (
            <div className="pub-vid" style={{ marginBottom: 20 }}>
              <img
                src={(post.featuredImageUrl ?? '').trim()}
                alt=""
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          )}
          {post.excerpt && (
            <div
              className="public-site-prose"
              style={{ marginBottom: 24, paddingLeft: 12, borderLeft: '3px solid #7c3aed' }}
              dangerouslySetInnerHTML={{ __html: post.excerpt }}
            />
          )}
          <div className="public-site-prose" dangerouslySetInnerHTML={{ __html: post.body }} />
        </article>
      </PortfolioSiteLayout>
    </>
  );
};

export default PortfolioBlogPostPage;
